'use client'

import { Send } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

interface ChatbotViewProps {
  micrositeId: string
  primaryColor: string
  textColor: string
  backgroundColor?: string
}

export const ChatbotView: React.FC<ChatbotViewProps> = ({
  micrositeId,
  primaryColor,
  textColor,
  backgroundColor = '#ffffff',
}) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendMessage = useCallback(async () => {
    const text = input.trim()
    if (!text || isLoading) return

    const userMessage: Message = { role: 'user', content: text }
    const updatedMessages = [...messages, userMessage]

    setMessages(updatedMessages)
    setInput('')
    setIsLoading(true)

    setMessages((prev) => [...prev, { role: 'assistant', content: '' }])

    try {
      const response = await fetch('/api/chatbot/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updatedMessages, micrositeId }),
      })

      if (!response.ok || !response.body) {
        setMessages((prev) => {
          const next = [...prev]
          next[next.length - 1] = {
            role: 'assistant',
            content: "Sorry, I couldn't process your request.",
          }
          return next
        })
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const chunk = decoder.decode(value, { stream: true })
        setMessages((prev) => {
          const next = [...prev]
          next[next.length - 1] = {
            role: 'assistant',
            content: (next[next.length - 1].content ?? '') + chunk,
          }
          return next
        })
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev]
        next[next.length - 1] = {
          role: 'assistant',
          content: 'Something went wrong. Please try again.',
        }
        return next
      })
    } finally {
      setIsLoading(false)
    }
  }, [input, isLoading, messages, micrositeId])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const inputBar = (
    <div className="relative flex items-center">
      <input
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Ask a question..."
        disabled={isLoading}
        className="w-full rounded-2xl border-2 px-5 py-3 pr-12 text-base outline-none transition-shadow focus:shadow-md disabled:opacity-50 bg-transparent"
        style={{ borderColor: primaryColor, color: textColor }}
      />
      <button
        type="button"
        onClick={sendMessage}
        disabled={isLoading || !input.trim()}
        className="absolute right-3 flex h-7 w-7 items-center justify-center rounded-full transition-opacity disabled:opacity-30"
        style={{ backgroundColor: primaryColor }}
      >
        <Send className="h-3.5 w-3.5 text-white" />
      </button>
    </div>
  )

  // Idle state — centered prompt
  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-2">
        <div className="text-center">
          <h2 className="text-xl font-semibold" style={{ color: textColor }}>
            How can I help you?
          </h2>
          <p className="mt-1 text-sm opacity-60" style={{ color: textColor }}>
            Ask me anything — I'll answer based on what I know.
          </p>
        </div>
        <div className="w-full max-w-sm">{inputBar}</div>
      </div>
    )
  }

  // Active state — thread scrolls, input fixed to viewport bottom
  return (
    <div>
      {/* Message thread — extra bottom padding clears the fixed input bar */}
      <div className="space-y-6 py-4 pb-28">
        {messages.map((message, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: messages have no stable id
            key={i}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.role === 'user' ? (
              <div
                className="max-w-[75%] rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm leading-relaxed"
                style={{ backgroundColor: primaryColor, color: '#ffffff' }}
              >
                {message.content}
              </div>
            ) : (
              <div className="max-w-[85%] text-sm leading-relaxed" style={{ color: textColor }}>
                {message.content ? (
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      a: ({ children, href }) => (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:opacity-75"
                          style={{ color: primaryColor }}
                        >
                          {children}
                        </a>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold">{children}</strong>
                      ),
                      table: ({ children }) => (
                        <div className="overflow-x-auto mb-2">
                          <table className="text-xs border-collapse w-full">{children}</table>
                        </div>
                      ),
                      th: ({ children }) => (
                        <th
                          className="border px-2 py-1 text-left font-semibold opacity-70"
                          style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                        >
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td
                          className="border px-2 py-1"
                          style={{ borderColor: 'rgba(0,0,0,0.15)' }}
                        >
                          {children}
                        </td>
                      ),
                    }}
                  >
                    {message.content}
                  </Markdown>
                ) : (
                  <span className="inline-flex gap-1 opacity-50">
                    <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
                      ·
                    </span>
                    <span className="animate-bounce" style={{ animationDelay: '150ms' }}>
                      ·
                    </span>
                    <span className="animate-bounce" style={{ animationDelay: '300ms' }}>
                      ·
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input — fixed to viewport bottom, floats over content with a fade */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 px-4"
        style={{ background: `linear-gradient(to bottom, transparent, ${backgroundColor} 40%)` }}
      >
        <div className="max-w-md mx-auto pb-4 pt-6">{inputBar}</div>
      </div>
    </div>
  )
}
